import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, ShieldCheck, MapPin, FileCheck } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
    "https://images.unsplash.com/photo-1603294278610-b5bd0506303e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb25kb24lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3ODk4MjQxODZ8MA&ixlib=rb-4.1.0&q=85";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1.1, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
    }),
};

const fade = {
    hidden: { opacity: 0, y: 24 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay: 0.9 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
    }),
};

const Hero = () => {
    const frameRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.32, 0.12]);

    const onMove = (e) => {
        const r = frameRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16 lg:pt-24"
        >
            {/* Parallax skyline backdrop */}
            <motion.div style={{ y: bgY, opacity: bgOpacity }} className="absolute inset-0" aria-hidden="true">
                <img
                    src={HERO_IMG}
                    alt=""
                    className="h-full w-full object-cover"
                    style={{ filter: "grayscale(1) brightness(0.7)" }}
                />
                <div className="absolute inset-0 bg-electric/20 mix-blend-color" />
                <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/60 to-navy" />
                <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/40 to-transparent" />
            </motion.div>

            {/* Glow orb */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 right-[-10%] h-[620px] w-[620px] rounded-full opacity-30"
                style={{
                    background: "radial-gradient(circle at center, rgba(59,130,246,0.5), rgba(245,158,11,0.12) 45%, transparent 70%)",
                    filter: "blur(40px)",
                }}
            />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
                {/* Copy */}
                <div className="lg:col-span-7">
                    <motion.div variants={fade} custom={0} initial="hidden" animate="show" className="mb-8 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-amber-400">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            Registered in England &amp; Wales
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
                            <MapPin className="h-3.5 w-3.5" />
                            Covent Garden, London
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
                    >
                        <span className="mask-line">
                            <motion.span variants={line} custom={0} initial="hidden" animate="show">
                                Your back office,
                            </motion.span>
                        </span>
                        <span className="mask-line">
                            <motion.span variants={line} custom={1} initial="hidden" animate="show">
                                running like{" "}
                                <span className="bg-gradient-to-r from-electric-glow to-amber-400 bg-clip-text text-transparent">
                                    clockwork.
                                </span>
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        data-testid="hero-subtext"
                        variants={fade}
                        custom={1}
                        initial="hidden"
                        animate="show"
                        className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                    >
                        Admin Logic Services Ltd provides dependable business administration and
                        back-office support for UK companies — from data, documents and diaries to
                        invoicing assistance and day-to-day operations.
                    </motion.p>

                    <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            data-testid="hero-cta-primary"
                            onClick={() => scrollToId("#contact")}
                            className="group flex items-center gap-3 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-navy transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_40px_rgba(245,158,11,0.35)]"
                        >
                            Start a conversation
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        <button
                            data-testid="hero-cta-secondary"
                            onClick={() => scrollToId("#services")}
                            className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-electric/60 hover:text-white"
                        >
                            What we handle
                        </button>
                    </motion.div>

                    <motion.div
                        variants={fade}
                        custom={3}
                        initial="hidden"
                        animate="show"
                        className="mt-12 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500"
                    >
                        <FileCheck className="h-4 w-4 text-electric" />
                        Company No. 17432590
                    </motion.div>
                </div>

                {/* 3D tilted photo frame */}
                <div className="hidden lg:col-span-5 lg:block" style={{ perspective: "1200px" }}>
                    <motion.div
                        variants={fade}
                        custom={2}
                        initial="hidden"
                        animate="show"
                        ref={frameRef}
                        onMouseMove={onMove}
                        onMouseLeave={onLeave}
                        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                        className="relative"
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -inset-10 animate-spin-slow rounded-full opacity-40"
                            style={{
                                background:
                                    "conic-gradient(from 0deg, transparent 0%, rgba(59,130,246,0.4) 12%, transparent 26%, transparent 55%, rgba(245,158,11,0.45) 66%, transparent 80%)",
                                filter: "blur(6px)",
                            }}
                        />
                        <div
                            className="duotone-frame relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_-20px_rgba(2,6,23,0.9)]"
                            style={{ transform: "translateZ(40px)" }}
                        >
                            <img
                                src="https://images.pexels.com/photos/8134169/pexels-photo-8134169.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Business administration work in progress"
                                className="duotone-img aspect-[4/5] w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/20" />
                            <span className="absolute left-4 top-4 h-5 w-5 border-l-2 border-t-2 border-amber-400" />
                            <span className="absolute right-4 top-4 h-5 w-5 border-r-2 border-t-2 border-amber-400" />
                            <span className="absolute bottom-4 left-4 h-5 w-5 border-b-2 border-l-2 border-amber-400" />
                            <span className="absolute bottom-4 right-4 h-5 w-5 border-b-2 border-r-2 border-amber-400" />
                            <div className="absolute inset-x-0 bottom-0 p-6">
                                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-400">
                                    Operating from London
                                </p>
                                <p className="mt-2 font-display text-lg font-bold text-white">
                                    Administration, handled with precision
                                </p>
                            </div>
                        </div>

                        <div
                            className="glass-card animate-float-y absolute -left-12 top-10 z-20 rounded-xl px-5 py-4 shadow-xl"
                            style={{ transform: "translateZ(70px)" }}
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                Documents &amp; Data
                            </p>
                            <p className="mt-1 font-display text-sm font-bold text-white">Organised, always</p>
                        </div>
                        <div
                            className="glass-card animate-float-y absolute -right-8 bottom-16 z-20 rounded-xl px-5 py-4 shadow-xl [animation-delay:1.4s]"
                            style={{ transform: "translateZ(70px)" }}
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                Diaries &amp; Invoicing
                            </p>
                            <p className="mt-1 font-display text-sm font-bold text-white">On time, every time</p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                onClick={() => scrollToId("#services")}
                aria-label="Scroll to services"
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-slate-500 transition-colors hover:text-amber-400"
            >
                <ArrowDown className="h-5 w-5 animate-bounce" />
            </motion.button>
        </section>
    );
};

export default Hero;
